import * as React from 'react';
import {
  Grid,
  Form,
  FormBody,
  FormFooter,
  FormGroup,
  Label,
  LabelText,
  InputMessage,
  InputAddonNumberControls,
  useUpDownNumberControl,
  InputAddonText,
  Input,
  Button,
  DropDown,
  InfoBox,
  themeUtils as tu,
} from '@zen-common/components-base';
import { FpUtils, TickerAutoSuggest } from '@zen/common-app-parts';
import _oracles from '../../utils/oracles';
import OraclesDropDownFG from '../form/edit/OraclesDropDownFG';
import useFPFormDate from './useFPFormDate';

const oracles = _oracles.map((o) => ({ label: `${o.chain} ${o.address}`, value: o.address }));

/**
 * A form for issue and cancel
 * @param {Object} props
 * @param {boolean} props.isCancel - is this a cancel form
 * @param {Function} props.onSubmit
 * @param {import('../../stores/FixedPayoutStore').IssueCancelStore} props.store
 */
export default function IssueCancelForm({ isCancel, onSubmit, store, naming }) {
  const {
    actions,
    state: {
      oracle,
      price,
      ticker,
      collateralAmount,
      collateralAsset,
      asset1,
      amount1,
      asset2,
      amount2,
      validForm: formValid,
    },
  } = store;
  const { dayMonthYear, days, months, years, error } = useFPFormDate({
    setDate: actions.setDate,
  });

  const setOracle = (item) => {
    actions.setOracle(_oracles.find((o) => o.address === item.value) || null);
  };
  const setTicker = (item) => {
    actions.setTicker(item);
  };
  const reset = () => {
    actions.reset();
    dayMonthYear.reset();
  };

  const isMobile = document.body.clientWidth < tu.breakpoints.xl.replace('px', '');

  const handleCollateralAmountChange = (value) => {
    actions.setCollateralAmount(value);
    if (isCancel) {
      actions.setAmount1(value);
      actions.setAmount2(value);
    }
  };

  const collateralAmountKeyHandler = useUpDownNumberControl({
    min: 0,
    value: collateralAmount.value.safeValue,
    onChange: handleCollateralAmountChange,
  });
  const priceKeyHandler = useUpDownNumberControl({
    min: 0,
    value: price.value.safeValue,
    onChange: actions.setPrice,
  });

  const resetDisabled = React.useMemo(
    () =>
      collateralAmount.value.value === '' &&
      asset1.value === '' &&
      asset2.value === '' &&
      ticker.value === '' &&
      price.value.value === '' &&
      dayMonthYear.year === null &&
      dayMonthYear.month === null &&
      dayMonthYear.day === null,
    [
      asset1.value,
      asset2.value,
      collateralAmount.value.value,
      dayMonthYear.day,
      dayMonthYear.month,
      dayMonthYear.year,
      price.value.value,
      ticker.value,
    ]
  );

  return (
    <Form onSubmit={onSubmit}>
      <FormBody>
        <OraclesDropDownFG items={oracles} value={oracle} onChange={setOracle} />

        <Grid.Row>
          <Grid.Col50>
            <FormGroup>
              <InfoBox info="Held by the FPC" isMobile={isMobile}>
                <Label htmlFor="issue-form-collateral-asset">
                  <LabelText>
                    {FpUtils.getTitle({
                      asset: collateralAsset.value,
                      title: 'Collateral',
                      naming,
                    })}
                  </LabelText>
                </Label>
              </InfoBox>
              <Input
                id="issue-form-collateral-asset"
                inputType="input"
                variant="block"
                value={collateralAsset.value}
                onChange={(e) => actions.setCollateralAsset(e.target.value.trim())}
                valid={collateralAsset.value ? collateralAsset.valid : undefined}
              />
              <InputMessage>
                {collateralAsset.value && !collateralAsset.valid && 'Invalid asset'}
              </InputMessage>
            </FormGroup>
          </Grid.Col50>
          <Grid.Col50>
            <FormGroup>
              <InfoBox info="Amount of collateral" isMobile={isMobile}>
                <Label htmlFor="issue-form-collateral-amount">
                  <LabelText>Amount</LabelText>
                </Label>
              </InfoBox>
              <Input
                id="issue-form-collateral-amount"
                inputType="input"
                variant="block"
                value={collateralAmount.value.display}
                onChange={(e) => handleCollateralAmountChange(e.target.value.trim())}
                valid={collateralAmount.value.display ? collateralAmount.valid : undefined}
                renderRightAddon={
                  <InputAddonNumberControls
                    onChange={handleCollateralAmountChange}
                    value={collateralAmount.value.safeValue}
                    min="0"
                  />
                }
                {...collateralAmountKeyHandler}
              />
              <InputMessage>
                {collateralAmount.value.display && !collateralAmount.valid && 'Invalid amount'}
              </InputMessage>
            </FormGroup>
          </Grid.Col50>
        </Grid.Row>

        {isCancel && (
          <>
            <Grid.Row>
              <Grid.Col50>
                <FormGroup>
                  <InfoBox info="Bull/Bear" isMobile={isMobile}>
                    <Label htmlFor="issue-form-asset1">
                      <LabelText>
                        {FpUtils.getTitle({ asset: asset1.value, title: 'Asset 1', naming })}
                      </LabelText>
                    </Label>
                  </InfoBox>
                  <Input
                    id="issue-form-asset1"
                    inputType="input"
                    variant="block"
                    value={asset1.value}
                    onChange={(e) => actions.setAsset1(e.target.value.trim())}
                    valid={asset1.value ? asset1.valid : undefined}
                  />
                  <InputMessage>
                    {asset1.value &&
                    !FpUtils.isSameEvent(naming[asset1.value], naming[asset2.value])
                      ? 'Assets does not match the same event'
                      : asset1.value && !asset1.valid && 'Invalid asset'}
                  </InputMessage>
                </FormGroup>
              </Grid.Col50>
              <Grid.Col50>
                <FormGroup>
                  <Label htmlFor="issue-form-amount1">
                    <LabelText>Amount</LabelText>
                  </Label>
                  <Input
                    id="issue-form-amount1"
                    inputType="input"
                    readOnly
                    disabled
                    variant="block"
                    value={amount1.value.display}
                  />
                  <InputMessage type="info"></InputMessage>
                </FormGroup>
              </Grid.Col50>
            </Grid.Row>
            <Grid.Row>
              <Grid.Col50>
                <FormGroup>
                  <InfoBox info="Bull/Bear" isMobile={isMobile}>
                    <Label htmlFor="issue-form-asset2">
                      <LabelText>
                        {FpUtils.getTitle({ asset: asset2.value, title: 'Asset 2', naming })}
                      </LabelText>
                    </Label>
                  </InfoBox>
                  <Input
                    id="issue-form-asset2"
                    inputType="input"
                    variant="block"
                    value={asset2.value}
                    onChange={(e) => actions.setAsset2(e.target.value.trim())}
                    valid={asset2.value ? asset2.valid : undefined}
                  />
                  <InputMessage>
                    {asset1.value &&
                    !FpUtils.isSameEvent(naming[asset1.value], naming[asset2.value])
                      ? 'Assets does not match the same event'
                      : asset2.value && !asset2.valid && 'Invalid asset'}
                  </InputMessage>
                </FormGroup>
              </Grid.Col50>
              <Grid.Col50>
                <FormGroup>
                  <Label htmlFor="issue-form-amount2">
                    <LabelText>Amount</LabelText>
                  </Label>
                  <Input
                    id="issue-form-amount2"
                    inputType="input"
                    readOnly
                    disabled
                    variant="block"
                    value={amount2.value.display}
                  />
                  <InputMessage type="info"></InputMessage>
                </FormGroup>
              </Grid.Col50>
            </Grid.Row>
          </>
        )}

        <Grid.Row>
          <Grid.Col50>
            <FormGroup>
              <InfoBox info="Available tickers from data source" isMobile={isMobile}>
                <Label htmlFor="ticker">
                  <LabelText>Ticker</LabelText>
                </Label>
              </InfoBox>
              <TickerAutoSuggest
                id="ticker"
                ticker={ticker.value}
                onTickerChanged={setTicker}
                tickerValid={ticker.valid}
              />
              <InputMessage></InputMessage>
            </FormGroup>
          </Grid.Col50>
          <Grid.Col50>
            <FormGroup>
              <Label htmlFor="strike-price">
                <LabelText>Strike Price (potential)</LabelText>
              </Label>
              <Input
                id="strike-price"
                variant="block"
                value={price.value.display}
                valid={price.value.display ? price.valid : undefined}
                autoComplete="off"
                type="text"
                onChange={(e) => actions.setPrice(e.target.value.trim())}
                renderLeftAddon={<InputAddonText>$</InputAddonText>}
                renderRightAddon={
                  <>
                    <InputAddonNumberControls
                      onChange={actions.setPrice}
                      value={price.value.safeValue}
                      min="0"
                    />
                  </>
                }
                {...priceKeyHandler}
              />
            </FormGroup>
            <InputMessage>
              {price.value.display && !price.valid ? 'Price invalid ' : ''}
            </InputMessage>
          </Grid.Col50>
        </Grid.Row>

        <FormGroup>
          <Grid.Row>
            <Grid.Col33>
              <Label htmlFor="expiry-date-year">
                <LabelText>Expiry Year</LabelText>
              </Label>
              <DropDown
                id="expiry-date-year"
                placeholder="Year"
                items={years}
                value={dayMonthYear.year}
                onChange={(item) => dayMonthYear.setYear(item.value)}
              />
            </Grid.Col33>
            <Grid.Col33>
              <Label htmlFor="expiry-date-month">
                <LabelText>Expiry Month</LabelText>
              </Label>
              <DropDown
                id="expiry-date-month"
                placeholder="Month"
                items={months}
                value={dayMonthYear.month}
                onChange={(item) => dayMonthYear.setMonth(item.value)}
              />
            </Grid.Col33>
            <Grid.Col33>
              <Label htmlFor="expiry-date-day">
                <LabelText>Expiry Day</LabelText>
              </Label>
              <DropDown
                id="expiry-date-day"
                placeholder="Day"
                items={days}
                value={dayMonthYear.day}
                onChange={(item) => dayMonthYear.setDay(item.value)}
              />
            </Grid.Col33>
          </Grid.Row>
          <InputMessage>{error && !isCancel ? 'The chosen date is in the past' : ''}</InputMessage>
        </FormGroup>

        <FormFooter>
          <Button bg="secondary" onClick={reset} disabled={resetDisabled}>
            Reset
          </Button>
          <Button type="submit" disabled={!formValid}>
            Generate
          </Button>
        </FormFooter>
      </FormBody>
    </Form>
  );
}
